import type { TOC } from '@ember/component/template-only';
import { hash } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { PrismIcon, PrismMessage } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { BREAKDOWNS } from 'lumen/utils/central-mapping-constants';

import StayRestrictionsAccordion, {
  type StayRestrictionsAccordionState,
} from './stay-restrictions-accordion';

interface StayRestrictionsPanelSignature {
  Args: {
    hotelState: StayRestrictionsAccordionState;
    showRateGroupsEmptyState: boolean;
  };
}

interface RateGroupSettingsLinkSignature {
  Args: {
    dataTestId?: string;
    label: string;
  };
}

/* Deep-links (new tab) into Central Mapping's rate groups breakdown from the
   empty-state CTA. */
const RateGroupSettingsLink = <template>
  <LinkTo
    @query={{hash breakdown=BREAKDOWNS.STAY_RESTRICTION_RATE_GROUP}}
    @route="hotel.settings.central-mapping.mapping"
    class="prism-link prism-link--small prism-link--standalone"
    data-test-id={{@dataTestId}}
    rel="noopener noreferrer"
    target="_blank"
  >
    <span class="flex items-center gap-50">
      {{@label}}
      <PrismIcon
        @color="inherit"
        @name="link-out"
        @size="small"
        aria-hidden="true"
      />
    </span>
  </LinkTo>
</template> satisfies TOC<RateGroupSettingsLinkSignature>;

const StayRestrictionsPanel = <template>
  <StayRestrictionsAccordion
    @id="hotel-min-los"
    @state={{@hotelState}}
    @title={{t "general.hotel"}}
  />
  {{#if @showRateGroupsEmptyState}}
    <StayRestrictionsAccordion
      @id="rate-groups-min-los"
      @showEmptyState={{true}}
      @title={{t "pricing_assistant.stay_restrictions.rate_groups"}}
    >
      <:emptyState>
        <div
          class="flex w-full justify-center py-500"
          data-test-id="rate-groups-empty-state"
        >
          <PrismMessage
            @description={{t
              "pricing_assistant.stay_restrictions.rate_groups_empty_state.description"
            }}
            @illustration="100x100_empty"
          >
            <:link>
              <RateGroupSettingsLink
                @dataTestId="rate-groups-empty-state-settings-link"
                @label={{t "general.go_to_settings"}}
              />
            </:link>
          </PrismMessage>
        </div>
      </:emptyState>
    </StayRestrictionsAccordion>
  {{/if}}
</template> satisfies TOC<StayRestrictionsPanelSignature>;

export default StayRestrictionsPanel;
